'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { JSX } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { useCommandLink } from '@/components/search/use-command-link';
import { CommandItem } from '@/components/ui/app-command';
import { StatusPulse } from '@/components/ui/status-pulse';
import { cn } from '@/lib/ui/utils';
export type LiveEventSearchPhase = 'live' | 'upcoming' | 'concluded';
export interface LiveEventSearchResult {
  id: string;
  title: string;
  imageUrl: string;
  phase: LiveEventSearchPhase;
  dateLabel: string;
}
interface LiveEventRowSearchItemProps {
  event: LiveEventSearchResult;
  onCommit: () => void;
}
const PHASE_CLASS: Readonly<Record<LiveEventSearchPhase, string>> = {
  live: 'text-feedback-error-content',
  upcoming: 'text-feedback-warning-content',
  concluded: 'text-foreground-secondary'
};
export function LiveEventRowSearchItem(t0) {
  const $ = _c(31);
  const {
    event,
    onCommit
  } = t0;
  const t = useTranslations("liveEvents.eventCard");
  const {
    onSelect,
    overlay
  } = useCommandLink(`/live/${event.id}`, onCommit);
  const t1 = event.id;
  let t2;
  if ($[0] !== event.imageUrl) {
    t2 = <OptimizedImage variant="thumbnail" src={event.imageUrl} alt="" size="size-11" rounded="rounded-overlay-item" nsfw={false} preview={false} />;
    $[0] = event.imageUrl;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== event.title) {
    t3 = <span className="truncate text-body font-medium text-foreground-primary">{event.title}</span>;
    $[2] = event.title;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = event.phase;
  const t5 = PHASE_CLASS[event.phase];
  let t6;
  if ($[4] !== t5) {
    t6 = cn("inline-flex shrink-0 items-center gap-1", t5);
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== event.phase) {
    t7 = event.phase === "live" ? <StatusPulse tone="error" /> : null;
    $[6] = event.phase;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== event.phase || $[9] !== t) {
    t8 = t(event.phase);
    $[8] = event.phase;
    $[9] = t;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== event.phase || $[12] !== t6 || $[13] !== t7 || $[14] !== t8) {
    t9 = <span data-phase={t4} className={t6}>{t7}{t8}</span>;
    $[11] = event.phase;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span aria-hidden="true">·</span>;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== event.dateLabel) {
    t11 = <span className="truncate">{event.dateLabel}</span>;
    $[17] = event.dateLabel;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t11 || $[20] !== t9) {
    t12 = <span className="flex min-w-0 items-center gap-1.5 text-caption text-foreground-secondary">{t9}{t10}{t11}</span>;
    $[19] = t11;
    $[20] = t9;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12 || $[23] !== t3) {
    t13 = <div className="flex min-w-0 flex-1 flex-col gap-0.5">{t3}{t12}</div>;
    $[22] = t12;
    $[23] = t3;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== event.id || $[26] !== onSelect || $[27] !== overlay || $[28] !== t13 || $[29] !== t2) {
    t14 = <CommandItem value={t1} onSelect={onSelect} className="relative gap-3 rounded-overlay-item px-3 py-2.5">{overlay}{t2}{t13}</CommandItem>;
    $[25] = event.id;
    $[26] = onSelect;
    $[27] = overlay;
    $[28] = t13;
    $[29] = t2;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  return t14;
}